import React from 'react';
import { ArrowLeft, Calendar, TrendingUp, Sparkles, Heart, Users, MapPin, AlertCircle, User } from 'lucide-react';
import { LifeContext, AIProfile, AIProfileSnapshot, Screen } from '../types';

interface EvolutionHistoryViewProps {
  context: LifeContext;
  onNavigate: (screen: Screen) => void;
}

const STRINGS = {
  Italian: {
    title: 'Evoluzione Completa',
    subtitle: 'La storia di come ti conosco',
    back: 'Torna al Profilo',
    version: 'Versione',
    noHistory: 'Nessuna evoluzione ancora',
    noHistoryDesc: 'Il tuo profilo evolverà man mano che condividi momenti',
    summary: 'Chi eri',
    values: 'Valori',
    goals: 'Obiettivi',
    people: 'Persone',
    places: 'Luoghi',
    traits: 'Tratti',
    challenges: 'Sfide',
    triggers: {
      onboarding: 'Configurazione iniziale',
      entry_analysis: 'Analisi momento',
      manual_edit: 'Modifica manuale',
      auto_evolution: 'Evoluzione automatica',
      milestone: 'Traguardo raggiunto',
      chat_insight: 'Insight dalla conversazione'
    }
  },
  English: {
    title: 'Complete Evolution',
    subtitle: 'The story of how I know you',
    back: 'Back to Profile',
    version: 'Version',
    noHistory: 'No evolution yet',
    noHistoryDesc: 'Your profile will evolve as you share moments',
    summary: 'Who you were',
    values: 'Values',
    goals: 'Goals',
    people: 'People',
    places: 'Places',
    traits: 'Traits',
    challenges: 'Challenges',
    triggers: {
      onboarding: 'Initial setup',
      entry_analysis: 'Moment analysis',
      manual_edit: 'Manual edit',
      auto_evolution: 'Automatic evolution',
      milestone: 'Milestone reached',
      chat_insight: 'Insight from conversation'
    }
  },
  Spanish: {
    title: 'Evolución Completa',
    subtitle: 'La historia de cómo te conozco',
    back: 'Volver al Perfil',
    version: 'Versión',
    noHistory: 'Sin evolución aún',
    noHistoryDesc: 'Tu perfil evolucionará mientras compartes momentos',
    summary: 'Quién eras',
    values: 'Valores',
    goals: 'Objetivos',
    people: 'Personas',
    places: 'Lugares',
    traits: 'Rasgos',
    challenges: 'Desafíos',
    triggers: {
      onboarding: 'Configuración inicial',
      entry_analysis: 'Análisis del momento',
      manual_edit: 'Edición manual',
      auto_evolution: 'Evolución automática',
      milestone: 'Logro alcanzado',
      chat_insight: 'Insight de la conversación'
    }
  },
  German: {
    title: 'Vollständige Entwicklung',
    subtitle: 'Die Geschichte, wie ich dich kenne',
    back: 'Zurück zum Profil',
    version: 'Version',
    noHistory: 'Noch keine Entwicklung',
    noHistoryDesc: 'Dein Profil wird sich entwickeln, während du Momente teilst',
    summary: 'Wer du warst',
    values: 'Werte',
    goals: 'Ziele',
    people: 'Menschen',
    places: 'Orte',
    traits: 'Merkmale',
    challenges: 'Herausforderungen',
    triggers: {
      onboarding: 'Ersteinrichtung',
      entry_analysis: 'Momentanalyse',
      manual_edit: 'Manuelle Bearbeitung',
      auto_evolution: 'Automatische Entwicklung',
      milestone: 'Meilenstein erreicht',
      chat_insight: 'Einsicht aus dem Gespräch'
    }
  },
  French: {
    title: 'Évolution Complète',
    subtitle: "L'histoire de comment je te connais",
    back: 'Retour au Profil',
    version: 'Version',
    noHistory: 'Pas encore d\'évolution',
    noHistoryDesc: 'Ton profil évoluera au fur et à mesure que tu partages des moments',
    summary: 'Qui tu étais',
    values: 'Valeurs',
    goals: 'Objectifs',
    people: 'Personnes',
    places: 'Lieux',
    traits: 'Traits',
    challenges: 'Défis',
    triggers: {
      onboarding: 'Configuration initiale',
      entry_analysis: 'Analyse du moment',
      manual_edit: 'Modification manuelle',
      auto_evolution: 'Évolution automatique',
      milestone: 'Jalon atteint',
      chat_insight: 'Insight de la conversation'
    }
  }
};

const EvolutionHistoryView: React.FC<EvolutionHistoryViewProps> = ({ context, onNavigate }) => {
  const lang = context.language === 'Italian' ? 'Italian' : 
               context.language === 'Spanish' ? 'Spanish' : 
               context.language === 'German' ? 'German' : 
               context.language === 'French' ? 'French' : 'English';
  const t = STRINGS[lang];

  const profile = context.aiProfile as AIProfile | undefined;
  const history = profile?.profileHistory || [];

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(lang === 'Italian' ? 'it-IT' : lang === 'Spanish' ? 'es-ES' : lang === 'German' ? 'de-DE' : lang === 'French' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="h-full flex flex-col bg-[#F5F5F7] view-enter overflow-hidden">
      <header className="px-6 lg:px-12 xl:px-16 2xl:px-20 pt-6 lg:pt-6 pb-4 lg:pb-5 shrink-0 bg-white/95 backdrop-blur-xl border-b border-black/[0.03]">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => onNavigate('insights')}
            className="flex items-center gap-2 text-black/50 hover:text-black/80 mb-4 transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">{t.back}</span>
          </button>
          <h1 className="text-[28px] lg:text-3xl font-black tracking-tighter leading-none text-[#1D1D1F] mb-1">{t.title}</h1>
          <p className="text-[13px] lg:text-sm text-black/40 font-medium">{t.subtitle}</p>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 lg:px-12 xl:px-20 2xl:px-32 py-6 lg:py-8 no-scrollbar pb-32 lg:pb-44">
        <div className="max-w-4xl mx-auto">
          {history.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <TrendingUp size={32} className="text-gray-400" />
              </div>
              <h2 className="text-xl font-black text-black mb-3">{t.noHistory}</h2>
              <p className="text-gray-600 text-base">{t.noHistoryDesc}</p>
            </div>
          ) : (
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 via-blue-500 to-gray-200"></div>
              
              <div className="space-y-8">
                {history.slice().reverse().map((snapshot, i) => (
                  <div key={i} className="relative pl-14">
                    <div className="absolute left-4 w-5 h-5 rounded-full bg-white border-2 border-purple-500 shadow-sm flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    </div>
                    
                    <div className="bg-white rounded-[24px] p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                            v{snapshot.version}
                          </span>
                          <span className="text-xs font-medium text-black/40">
                            {t.triggers[snapshot.trigger as keyof typeof t.triggers] || snapshot.trigger}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-black/30">
                          <Calendar size={12} />
                          <span className="text-[11px] font-medium">{formatDate(snapshot.timestamp)}</span>
                        </div>
                      </div>
                      
                      {snapshot.summary && (
                        <div className="mb-4 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl">
                          <div className="flex items-center gap-2 mb-2">
                            <Heart size={14} className="text-pink-500" />
                            <span className="text-[10px] font-bold text-black/40 uppercase">{t.summary}</span>
                          </div>
                          <p className="text-[14px] text-black/70 leading-relaxed">{snapshot.summary}</p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                        {snapshot.coreValues && snapshot.coreValues.length > 0 && (
                          <div className="p-3 bg-[#F5F5F7] rounded-xl">
                            <div className="flex items-center gap-1.5 mb-2">
                              <Sparkles size={12} className="text-purple-500" />
                              <span className="text-[10px] font-bold text-black/40 uppercase">{t.values}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {snapshot.coreValues.slice(0, 3).map((v, j) => (
                                <span key={j} className="text-[11px] font-medium text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">
                                  {v.name}
                                </span>
                              ))}
                              {snapshot.coreValues.length > 3 && (
                                <span className="text-[11px] text-black/40">+{snapshot.coreValues.length - 3}</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {snapshot.lifeGoals && snapshot.lifeGoals.length > 0 && (
                          <div className="p-3 bg-[#F5F5F7] rounded-xl">
                            <div className="flex items-center gap-1.5 mb-2">
                              <TrendingUp size={12} className="text-blue-500" />
                              <span className="text-[10px] font-bold text-black/40 uppercase">{t.goals}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {snapshot.lifeGoals.slice(0, 2).map((g, j) => (
                                <span key={j} className="text-[11px] font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full truncate max-w-[100px]">
                                  {g.goal}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {snapshot.keyPeople && snapshot.keyPeople.length > 0 && (
                          <div className="p-3 bg-[#F5F5F7] rounded-xl">
                            <div className="flex items-center gap-1.5 mb-2">
                              <Users size={12} className="text-emerald-500" />
                              <span className="text-[10px] font-bold text-black/40 uppercase">{t.people}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {snapshot.keyPeople.slice(0, 3).map((p, j) => (
                                <span key={j} className="text-[11px] font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                  {p.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {snapshot.personalityTraits && snapshot.personalityTraits.length > 0 && (
                          <div className="p-3 bg-[#F5F5F7] rounded-xl">
                            <div className="flex items-center gap-1.5 mb-2">
                              <User size={12} className="text-indigo-500" />
                              <span className="text-[10px] font-bold text-black/40 uppercase">{t.traits}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {snapshot.personalityTraits.slice(0, 3).map((t, j) => (
                                <span key={j} className="text-[11px] font-medium text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
                                  {t.trait}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {snapshot.blockers && snapshot.blockers.length > 0 && (
                          <div className="p-3 bg-[#F5F5F7] rounded-xl col-span-2">
                            <div className="flex items-center gap-1.5 mb-2">
                              <AlertCircle size={12} className="text-amber-500" />
                              <span className="text-[10px] font-bold text-black/40 uppercase">{t.challenges}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {snapshot.blockers.slice(0, 3).map((b, j) => (
                                <span key={j} className="text-[11px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                  {b.blocker}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvolutionHistoryView;
